import { useState } from "react";
import LoadingSkeleton from "@/components/LoadingSkeleton/LoadingSkeleton";
import { Blogs } from "@/components/api/zod-types"
import { useQuery } from "@tanstack/react-query";
import axios from "axios"
import BlogItemCard from "./BlogItemCard";
import EmailSubscribe from "../Subscribe/EmailSubcribe";
import Footer from "@/components/footer/footer";
import { Section } from "@/components/ui/section";
import { ScrollContainer } from "@/components/ui/scroll-container";
import { Search, FileText, Filter } from "lucide-react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GetCaseStudies = async () => {
    const response = await axios.get<Blogs>(`https://www.harveynormancommercial.nz/api/v1/blog/?categoryid=e023fe3f-6174-ee11-8179-0022489338ed`)
    return response.data;
}

export default function CaseStudies() {
    const location = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    
    const [searchQuery, setSearchQuery] = useState(""); //ss:9/1/25

    const { data: caseStudeis, isLoading, isFetching } = useQuery({
        queryKey: ["caseStudies"],
        queryFn: () => GetCaseStudies(),
        staleTime: 1000 * 60 * 60 * 24,
        gcTime: 1000 * 60 * 60 * 24
    })

    
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };
    const filteredCaseStudies = caseStudeis?.results.filter((item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
    

    return (
        <ScrollContainer snapType="y" snapBehavior="mandatory">
            {/* Header Section */}
            <Section
                scrollSnap={true}
                overlayColor="transparent"
                variant="light"
                padding="xl"
                className="bg-gradient-to-br from-gray-50 via-white to-green-50/30"
            >
                <div className="max-w-7xl mx-auto py-4">
                    {/* Header with Search */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-12 pt-8">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-harvey-red rounded-xl flex items-center justify-center shadow-lg">
                                    <FileText className="w-6 h-6 text-white" />
                                </div>
                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900">
                                    Case <span className="text-gradient-red">Studies</span>
                                </h1>
                            </div>
                            <p className="text-lg text-gray-600 max-w-2xl text-left">
                                Discover real-world success stories and learn how we've helped businesses achieve their goals.
                            </p>
                        </div>

                        {/* Modern Search Bar */}
                        <div className="relative w-full lg:w-auto lg:min-w-80">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-5 w-5 text-gray-400" />
                            </div>                            
                            <input
                                type="text"
                                placeholder="Search case studies..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="text-black w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-harvey-red/20 focus:border-harvey-red transition-all duration-200 shadow-sm hover:shadow-md"
                            />
                            {searchQuery && (
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    <Filter className="h-4 w-4 text-harvey-red" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stats/Info Bar */}
                    <div className="flex items-center justify-between bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 shadow-sm mb-8">
                        <div className="flex items-center gap-4">
                            <div className="text-sm text-gray-600">
                                {caseStudeis?.results ? (
                                    <>
                                        <span className="font-semibold text-gray-900">{filteredCaseStudies?.length || 0}</span> of{" "}
                                        <span className="font-semibold text-gray-900">{caseStudeis.results.length}</span> case studies
                                        {searchQuery && (
                                            <span className="ml-2 text-harvey-red">
                                                for "{searchQuery}"
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    "Loading case studies..."
                                )}
                            </div>
                        </div>
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="text-sm text-gray-500 hover:text-harvey-red transition-colors duration-200 flex items-center gap-1"
                            >
                                Clear search
                            </button>
                        )}
                    </div>

                    {/* Content Grid */}
                    {isLoading || isFetching ? (
                        <div className="py-12">
                            <LoadingSkeleton message="Loading case studies..." />
                        </div>
                    ) : (
                        <div className="grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-1 gap-8">                            
                            {filteredCaseStudies
                            ?.slice()
                            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                            .map((item, index) => (
                                // <BlogItemCard item={item} key={index} />
                                <BlogItemCard item={{ ...item, type: "casestudy" }} key={index} />
                            ))}
                        </div>
                    )}

                    {/* No Results Message */}
                    {caseStudeis && filteredCaseStudies?.length === 0 && searchQuery && (
                        <div className="text-center py-16">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Search className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No case studies found</h3>
                            <p className="text-gray-600 mb-6">
                                We couldn't find any case studies matching "{searchQuery}". Try a different search term.
                            </p>
                            <button
                                onClick={() => setSearchQuery("")}
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-harvey-red hover:bg-harvey-red/90 transition-colors duration-200"
                            >
                                Show all case studies
                            </button>
                        </div>
                    )}
                </div>
            </Section>
        </ScrollContainer>
    )
}